import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  TrendingUp,
  TrendingDown,
  XCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  Activity
} from 'lucide-react';
import type { ErrorAnalysis } from '@/types/ai-admin';

interface ErrorAnalysisPanelProps {
  errorAnalysis: ErrorAnalysis;
  totalQueries: number;
}

export default function ErrorAnalysisPanel({ errorAnalysis, totalQueries }: ErrorAnalysisPanelProps) {
  // Calculate error rate percentage
  const errorRate = (errorAnalysis.totalErrors / totalQueries) * 100;
  const successRate = 100 - errorRate;

  // Determine trend direction (mock - in real app would compare to previous period)
  const errorTrend = -2.3; // Mock: errors decreased by 2.3%
  const isImproving = errorTrend < 0;

  // Get error severity color
  const getErrorSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  // Get error type icon
  const getErrorIcon = (errorType: string) => {
    if (errorType.includes('timeout')) {
return Clock;
}
    if (errorType.includes('rate')) {
return Activity;
}
    if (errorType.includes('auth')) {
return AlertCircle;
}
    if (errorType.includes('validation')) {
return XCircle;
}
    return AlertTriangle;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Error Analysis</CardTitle>
        <CardDescription>
          System errors and performance issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Rate Overview */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium">Error Rate</h4>
              <Badge
                variant="outline"
                className={isImproving ? 'text-green-600' : 'text-red-600'}
              >
                {isImproving ? (
                  <TrendingDown className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingUp className="h-3 w-3 mr-1" />
                )}
                {Math.abs(errorTrend).toFixed(1)}%
              </Badge>
            </div>
            <span className="text-2xl font-bold">
              {errorRate.toFixed(1)}%
            </span>
          </div>
          <Progress
            value={successRate}
            className="h-2"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{errorAnalysis.totalErrors} errors</span>
            <span>{totalQueries - errorAnalysis.totalErrors} successful</span>
          </div>
        </div>

        {/* Error Breakdown by Type */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Error Breakdown</h4>
          <div className="space-y-2">
            {errorAnalysis.errorBreakdown?.map((error, index) => {
              const ErrorIcon = getErrorIcon(error.errorType);
              const percentage = (error.count / errorAnalysis.totalErrors) * 100;

              return (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ErrorIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{error.errorType}</span>
                      <Badge
                        variant="outline"
                        className={getErrorSeverityColor(error.severity || 'low')}
                      >
                        {error.severity || 'low'}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">{error.count}</span>
                      <span className="text-xs text-muted-foreground">
                        ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                  <Progress value={percentage} className="h-1" />
                  {error.affectedAgents && error.affectedAgents.length > 0 && (
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-muted-foreground">Affects:</span>
                      {error.affectedAgents.map((agent, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {agent}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Common Error Messages */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Recent Error Messages</h4>
          <div className="space-y-2">
            {errorAnalysis.commonErrors?.slice(0, 3).map((error, index) => (
              <div key={index} className="p-2 rounded-lg bg-muted/50 space-y-1">
                <div className="flex items-start justify-between">
                  <code className="text-xs font-mono text-red-600 dark:text-red-400">
                    {error.message}
                  </code>
                  <Badge variant="outline" className="text-xs ml-2">
                    {error.count}x
                  </Badge>
                </div>
                {error.lastOccurred && (
                  <p className="text-xs text-muted-foreground">
                    Last occurred: {new Date(error.lastOccurred).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        {errorRate > 2 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Action Required</AlertTitle>
            <AlertDescription className="mt-2 space-y-2">
              <p className="text-sm">
                Error rate is above acceptable threshold (2%). Consider:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1">
                {errorAnalysis.errorBreakdown?.[0]?.errorType.toLowerCase().includes('timeout') && (
                  <li>Increase timeout limits or optimize slow queries</li>
                )}
                {errorAnalysis.errorBreakdown?.[0]?.errorType.toLowerCase().includes('rate') && (
                  <li>Implement request throttling or upgrade API limits</li>
                )}
                {errorAnalysis.errorBreakdown?.[0]?.errorType.toLowerCase().includes('validation') && (
                  <li>Review input validation and error handling</li>
                )}
                <li>Enable detailed error logging for root cause analysis</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Indicator */}
        {errorRate < 2 && (
          <div className="flex items-center space-x-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                System Healthy
              </p>
              <p className="text-xs text-green-700 dark:text-green-200">
                Error rate is within acceptable limits ({errorRate.toFixed(1)}% &lt; 2%)
              </p>
            </div>
          </div>
        )}

        {/* Time Distribution */}
        <div className="pt-2 border-t space-y-2">
          <h4 className="text-sm font-medium">Error Time Distribution</h4>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2 rounded bg-muted/50">
              <p className="text-xs text-muted-foreground">Morning</p>
              <p className="text-sm font-medium">
                {Math.floor(errorAnalysis.totalErrors * 0.15)}
              </p>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <p className="text-xs text-muted-foreground">Afternoon</p>
              <p className="text-sm font-medium">
                {Math.floor(errorAnalysis.totalErrors * 0.45)}
              </p>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <p className="text-xs text-muted-foreground">Evening</p>
              <p className="text-sm font-medium">
                {Math.floor(errorAnalysis.totalErrors * 0.30)}
              </p>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <p className="text-xs text-muted-foreground">Night</p>
              <p className="text-sm font-medium">
                {Math.floor(errorAnalysis.totalErrors * 0.10)}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Import Clock since it's used in the component
import { Clock } from 'lucide-react';